import { Component, Inject, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { DocumentComponent } from 'app/components/reusable/document/document.component';
import { IHemoTechChange } from 'app/models/licenses/hemo-tech/hemo-tech-change';
import { HemoTechChangeService } from 'app/services/license/hemo-tech/hemo-tech-change.service';

export interface IHemoTechChangeDialog {
  licenseId: number;
  currentName: string;
}

@Component({
  selector: 'app-hemo-tech-change',
  templateUrl: './hemo-tech-change.component.html',
  styleUrls: ['./hemo-tech-change.component.scss']
})
export class HemoTechChangeComponent implements OnInit {
  @ViewChild(DocumentComponent) documents!: DocumentComponent;

  public changeSubmitted: boolean = false;
  public pendingChange: boolean = false;
  public documentTypes: string[] = ['Identification'];
  public changeForm = new FormGroup({
    firstName: new FormControl<string | null>(null, Validators.required),
    middleName: new FormControl<string | null>(null),
    lastName: new FormControl<string | null>(null, Validators.required)
  });
  public changes: IHemoTechChange[] = [];
  public change: IHemoTechChange = {
    id: 0,
    firstName: '',
    middleName: '',
    lastName: '',
    entity: '',
    originalValue: '',
    insufficient: false,
    approved: false,
    denied: false,
    completed: false,
    hemoTechLicenseId: 0
  }

  public changeColumns: string[] = ['firstName', 'originalValue', 'approved', 'completed', 'actions'];
  public changeSource = new MatTableDataSource<IHemoTechChange>(this.changes);

  public sharedService: SharedService = inject(SharedService);
  public service: HemoTechChangeService  = inject(HemoTechChangeService);
  public dialog = inject(MatDialog);

  constructor(@Inject(MAT_DIALOG_DATA) public changeData: IHemoTechChangeDialog,
    private dialogRef: MatDialogRef<HemoTechChangeComponent>) {}

  ngOnInit(): void {
    this.getChanges();
  }

  getChanges(): void {
    this.service.getChanges(this.changeData.licenseId).subscribe({
      next: (changes) => this.changes = changes,
      error: (err) => console.error('Error fetching changes:', err),
      complete: () => {
        this.updateChangeTable();
        this.pendingChange = this.changes.some(c => !c.completed);
        if(!this.pendingChange){
          this.createChange();
        }
      }
    });
  }

  updateChangeTable(): void {
    setTimeout(() => {
      this.changeSource.data = this.changes;
    }, 500);
  }

  createChange(): void {
    this.service.createChange(this.changeData.licenseId).subscribe({
      next: (change) => this.change = change,
      error: (err) => console.error('Error creating change:', err)
    });
  }

  submitChange(): void {
    if(this.documents.documents.length === 0){
      this.sharedService.openNotice('Please upload current identification, or proof of legal change of name.');
      return;
    }
    const form = this.changeForm.getRawValue();
    this.change.firstName = form.firstName ?? '';
    this.change.middleName = form.middleName ?? '';
    this.change.lastName = form.lastName ?? '';
    this.change.originalValue = this.changeData.currentName;

    this.service.updateChange(this.change).subscribe({
      next: (updatedChange) => {
        this.change = updatedChange;
        this.getChanges();
      },
      error: (err) => console.error('Error updating change:', err),
      complete: () => {
        this.changeSubmitted = true;
        this.changes.push(this.change);
        this.updateChangeTable();
        this.change = this.getEmptyChange();
        this.changeForm.reset({
          firstName: null,
          middleName: null,
          lastName: null
        });
      }
    });
  }

  getEmptyChange(): IHemoTechChange {
    return {
      id: 0,
      firstName: '',
      middleName: '',
      lastName: '',
      entity: '',
      originalValue: '',
      insufficient: false,
      approved: false,
      denied: false,
      completed: false,
      hemoTechLicenseId: 0
    };
  }

  deleteChange(changeId: number): void {
    this.sharedService.openConfirm('Are you sure you want to delete this name change request?');
    this.sharedService.confirmed().subscribe(confirmed => {
      if (confirmed) {
         this.confirmDelete(changeId);
      }
    });
  }

  private confirmDelete(changeId: number): void {
    this.service.deleteChange(changeId).subscribe({
      next: (success) => {
        if (success) {
          this.changes = this.changes.filter(c => c.id !== changeId);
        }
      },
      error: (err) => console.error('Error deleting change:', err),
      complete: () => {
        this.updateChangeTable();
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  cancel(): void {
    if(!this.changeSubmitted){
      this.confirmDelete(this.change.id);
      this.changeSubmitted = false;
      this.dialogRef.close();
    }
  }

}
